import api from './api'

class PermissionService {
  constructor() {
    this.userPermissions = null
    this.hasFullAccess = false
    this.lastLoadTime = null
    this.cacheTimeout = 5 * 60 * 1000 // 5 minutos
  }

  async loadUserPermissions() {
    // Verificar se já temos permissões em cache e se não expiraram
    if (this.userPermissions && this.lastLoadTime && 
        (Date.now() - this.lastLoadTime) < this.cacheTimeout) {
      return this.userPermissions
    }

    try {
      const response = await api.get('/user/page-access/')
      this.userPermissions = response.data
      this.hasFullAccess = response.data.has_full_access
      this.lastLoadTime = Date.now()
      return response.data
    } catch (error) {
      console.error('Erro ao carregar permissões do usuário:', error)
      // Em caso de erro, manter as permissões existentes se houver
      if (!this.userPermissions) {
        // Se não há permissões carregadas, retornar null para permitir fallback
        return null
      }
      return this.userPermissions
    }
  }

  async checkPagePermission(pageName) {
    try {
      const response = await api.get(`/check-permission/${pageName}/`)
      return response.data
    } catch (error) {
      console.error('Erro ao verificar permissão da página:', error)
      return {
        has_access: false,
        can_view: false,
        can_edit: false,
        can_create: false,
        can_delete: false
      }
    }
  }

  hasAccess(pageName) {
    // Páginas básicas sempre acessíveis para todos os usuários
    const basicPages = ['dashboard', 'favorites', 'profile']
    if (basicPages.includes(pageName)) {
      return true
    }
    
    if (this.hasFullAccess) {
      return true
    }
    
    if (!this.userPermissions || !this.userPermissions.accessible_pages) {
      return false
    }
    
    return this.userPermissions.accessible_pages.some(page => page.page === pageName)
  }

  canView(pageName) {
    // Páginas básicas sempre acessíveis para todos os usuários
    const basicPages = ['dashboard', 'favorites', 'profile']
    if (basicPages.includes(pageName)) {
      return true
    }
    
    if (this.hasFullAccess) {
      return true
    }
    
    if (!this.userPermissions || !this.userPermissions.accessible_pages) {
      return false
    }
    
    const pageAccess = this.userPermissions.accessible_pages.find(page => page.page === pageName)
    return pageAccess && (pageAccess.access_type === 'view' || pageAccess.access_type === 'both')
  }

  canEdit(pageName) {
    // Páginas básicas sempre acessíveis para todos os usuários
    const basicPages = ['dashboard', 'favorites', 'profile']
    if (basicPages.includes(pageName)) {
      return true
    }
    
    if (this.hasFullAccess) {
      return true
    }
    
    if (!this.userPermissions || !this.userPermissions.accessible_pages) {
      return false
    }
    
    const pageAccess = this.userPermissions.accessible_pages.find(page => page.page === pageName)
    return pageAccess && (pageAccess.access_type === 'edit' || pageAccess.access_type === 'both')
  }

  canCreate(pageName) {
    if (this.hasFullAccess) {
      return true
    }
    
    // Para permissões específicas, seria necessário implementar verificação adicional
    // Por enquanto, retorna true se pode editar
    return this.canEdit(pageName)
  }

  canDelete(pageName) {
    if (this.hasFullAccess) {
      return true
    }
    
    // Para permissões específicas, seria necessário implementar verificação adicional
    // Por enquanto, retorna true se pode editar
    return this.canEdit(pageName)
  }

  getAccessiblePages() {
    const basicPages = ['dashboard', 'favorites', 'profile']
    
    if (this.hasFullAccess) {
      return [
        ...basicPages,
        'reports', 'reports_list', 'financeiro', 'produtos', 'meta', 'sales_channels',
        'sales_representatives', 'sales_goals', 'page_management', 'franquias'
      ]
    }
    
    if (!this.userPermissions || !this.userPermissions.accessible_pages) {
      return basicPages
    }
    
    const customPages = this.userPermissions.accessible_pages.map(page => page.page)
    return [...basicPages, ...customPages]
  }

  clearPermissions() {
    this.userPermissions = null
    this.hasFullAccess = false
    this.lastLoadTime = null
  }

  clearCache() {
    this.lastLoadTime = null
  }
}

export default new PermissionService() 